import * as React from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  type InAppCard as InAppCardData,
  cardIconPath,
  inAppHref,
  isLinkableProjectSlug,
  useCardIcon,
} from "@/api/apps";
import { useSession } from "@/api/auth";
import { type Project, useProjects } from "@/api/projects";
import { ProjectChooserDialog } from "@/components/apps/ProjectChooserDialog";
import { buttonVariants } from "@/components/ui/button";
import { cardVariants } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useLastAppStore } from "@/stores/last-app";
import { rememberedSlugFor, useLastProjectStore } from "@/stores/last-project";
import { resolveTheme, useThemeStore } from "@/stores/theme";

/**
 * How an in-app card opens, decided from the viewer's projects (C8):
 *
 * - one valid project — a plain link to it, no chooser;
 * - more than one — a button opening {@link ProjectChooserDialog}, with the
 *   last-used project preselected when it is still valid, else the first by
 *   name;
 * - none, or a card whose target the SPA cannot link — disabled, with the
 *   reason on the card.
 *
 * "Valid" is `GET /api/projects` ∩ the link charset: the remembered slug is
 * only ever a preference among those, never a project on its own.
 */
export type InAppLaunch =
  | { kind: "link"; href: string; project: Project }
  | { kind: "chooser"; projects: Project[]; projectSlugs: string[]; preselected: string }
  | { kind: "disabled"; reason: string };

export function useInAppLaunch(card: InAppCardData): InAppLaunch {
  const { data: session } = useSession();
  const projects = useProjects();
  const last = useLastProjectStore((s) => s.last);

  if (projects.isPending) return { kind: "disabled", reason: "Loading your projects…" };
  if (projects.isError) return { kind: "disabled", reason: "Couldn't load your projects." };

  const listed = projects.data.map((p) => p.slug);
  const valid = projects.data
    .filter((p) => isLinkableProjectSlug(p.slug, listed))
    .sort((a, b) => a.display_name.localeCompare(b.display_name));
  if (valid.length === 0) {
    return { kind: "disabled", reason: `No project to open ${card.display_name} in` };
  }

  const projectSlugs = valid.map((p) => p.slug);
  const remembered = rememberedSlugFor(last, session);
  const preselected =
    remembered !== null && projectSlugs.includes(remembered) ? remembered : projectSlugs[0];
  const href = inAppHref(card.target, preselected, projectSlugs);
  if (href === null) return { kind: "disabled", reason: "This card's link is invalid" };

  if (valid.length === 1) return { kind: "link", href, project: valid[0] };
  return { kind: "chooser", projects: valid, projectSlugs, preselected };
}

/**
 * Records, for this viewer, that the card `slug` was opened now — what the
 * dashboard's hero reads as the app used last (`stores/last-app.ts`). Called
 * only when the card navigates. With no session yet there is no viewer to
 * key the entry to, so nothing is recorded.
 */
function useRememberApp(slug: string): () => void {
  const { data: session } = useSession();
  const remember = useLastAppStore((s) => s.remember);
  return () => {
    if (!session) return;
    remember({
      user_id: session.user_id,
      tenant_id: session.tenant_id,
      slug,
      opened_at: new Date().toISOString(),
    });
  };
}

/**
 * One in-app card — a FlowStack app that opens in this tab (C11).
 *
 * - `tile`: the Apps page's "FlowStack apps" card — a logo well like the Suite
 *   cards', the name with an "Opens here" badge where a Suite card shows its
 *   domain, the tagline and the action.
 * - `hero`: the dashboard's "Start work" card — the logo, the tagline, a
 *   "FlowStack app · opens here" badge and a `Run <name> →` action.
 *
 * The whole card is the control, as the Suite cards are: a `<Link>`, or a
 * `<button>` when it opens the chooser. Its content is phrasing elements only,
 * since a button may hold nothing else. Every href comes from `inAppHref`.
 */
export function InAppCard({
  card,
  variant,
}: {
  card: InAppCardData;
  variant: "tile" | "hero";
}): JSX.Element {
  const launch = useInAppLaunch(card);
  const rememberOpened = useRememberApp(card.slug);
  const navigate = useNavigate();
  const [chooserOpen, setChooserOpen] = React.useState(false);
  const descriptionId = React.useId();
  const action = `Run ${card.display_name}`;

  const body =
    variant === "hero" ? (
      <HeroBody card={card} launch={launch} descriptionId={descriptionId} />
    ) : (
      <TileBody card={card} launch={launch} descriptionId={descriptionId} />
    );
  const chrome = cn(
    cardVariants({ elevation: "raised" }),
    "group w-full rounded-2xl text-left",
    variant === "hero"
      ? "flex flex-wrap items-center justify-between gap-6 p-card"
      : "flex h-full flex-col overflow-hidden p-0"
  );
  const interactive = cn(
    chrome,
    "outline-none transition-all duration-200",
    "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
    "hover:-translate-y-0.5 hover:border-cyan/40 hover:shadow-[var(--elevation-accent)]"
  );

  if (launch.kind === "disabled") {
    return (
      <div
        role="link"
        aria-disabled="true"
        aria-label={action}
        aria-describedby={descriptionId}
        className={cn(chrome, "opacity-70")}
      >
        {body}
      </div>
    );
  }

  if (launch.kind === "link") {
    return (
      <Link
        to={launch.href}
        onClick={rememberOpened}
        aria-label={`${action} in ${launch.project.display_name}`}
        aria-describedby={descriptionId}
        className={interactive}
      >
        {body}
      </Link>
    );
  }

  const onOpenProject = (slug: string): void => {
    const href = inAppHref(card.target, slug, launch.projectSlugs);
    if (href === null) return;
    setChooserOpen(false);
    rememberOpened();
    navigate(href);
  };

  return (
    <>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-label={`${action}: choose a project`}
        aria-describedby={descriptionId}
        onClick={() => setChooserOpen(true)}
        className={interactive}
      >
        {body}
      </button>
      <ProjectChooserDialog
        open={chooserOpen}
        onOpenChange={setChooserOpen}
        appName={card.display_name}
        projects={launch.projects}
        preselected={launch.preselected}
        onOpenProject={onOpenProject}
      />
    </>
  );
}

interface BodyProps {
  card: InAppCardData;
  launch: InAppLaunch;
  descriptionId: string;
}

function TileBody({ card, launch, descriptionId }: BodyProps): JSX.Element {
  return (
    <>
      <span className="flex h-[132px] w-full items-center justify-center bg-panel-well px-10 py-6">
        <CardLogo card={card} className="max-h-16 w-full object-contain" />
      </span>
      <span className="flex w-full flex-1 flex-col gap-2 border-t border-border-subtle p-4 sm:px-5">
        <span className="flex items-center justify-between gap-3">
          <span className="font-display text-base font-semibold leading-tight text-foreground">
            {card.display_name}
          </span>
          <OpensHereBadge>Opens here</OpensHereBadge>
        </span>
        <Description card={card} launch={launch} id={descriptionId} className="text-sm" />
        {launch.kind === "disabled" ? null : (
          <span className="text-sm font-semibold text-accent" aria-hidden>
            {`Run ${card.display_name} →`}
          </span>
        )}
      </span>
    </>
  );
}

function HeroBody({ card, launch, descriptionId }: BodyProps): JSX.Element {
  return (
    <>
      <span className="flex min-w-0 flex-col gap-2.5">
        <CardLogo card={card} className="h-10 w-auto max-w-full self-start object-contain" />
        <Description
          card={card}
          launch={launch}
          id={descriptionId}
          className="max-w-[46ch] text-sm"
        />
        <OpensHereBadge>FlowStack app · opens here</OpensHereBadge>
      </span>
      {launch.kind === "disabled" ? null : (
        <span className={buttonVariants({ size: "lg", className: "px-5" })} aria-hidden>
          {`Run ${card.display_name} →`}
        </span>
      )}
    </>
  );
}

/** The tagline, and under it why the card cannot open when it cannot. It is
 * the control's description, so a screen reader hears the reason too. */
function Description({
  card,
  launch,
  id,
  className,
}: {
  card: InAppCardData;
  launch: InAppLaunch;
  id: string;
  className: string;
}): JSX.Element {
  return (
    <span id={id} className={cn("flex flex-col gap-1 text-muted-foreground", className)}>
      <span>{card.tagline}</span>
      {launch.kind === "disabled" ? (
        <span className="font-medium text-foreground">{launch.reason}</span>
      ) : null}
    </span>
  );
}

function OpensHereBadge({ children }: { children: React.ReactNode }): JSX.Element {
  return (
    <span className="inline-flex shrink-0 self-start rounded-full bg-cyan/15 px-2.5 py-0.5 text-xs font-semibold text-cyan-fg">
      {children}
    </span>
  );
}

/**
 * The card's logo for the theme on screen, fetched through the API client
 * (`useCardIcon`). Until it loads — or if it cannot — the name stands in, so
 * the card never shows a broken image.
 */
function CardLogo({ card, className }: { card: InAppCardData; className: string }): JSX.Element {
  const theme = useThemeStore((s) => s.theme);
  const src = useCardIcon(cardIconPath(card.icon, resolveTheme(theme)));
  if (src === null) {
    return (
      <span className="font-display text-2xl font-semibold text-foreground" aria-hidden>
        {card.display_name}
      </span>
    );
  }
  return <img src={src} alt="" className={className} />;
}
